import { useMemo, useRef, useState } from "react";
import { Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";

import { type CarrierTask, useReceiveTasksBulk } from "@/api/carrier";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TaskReferenceDisplay } from "@/components/task-reference-display";
import { formatTaskReadyAt } from "@/features/task-schedule-display";
import { formatDateOnly, formatDateTime } from "@/lib/format";
import { DispatchInlineState } from "./dispatch-state";
import { taskLabel } from "./dispatch-presenters";

interface ReceiptGroup {
  centerId: string | null;
  centerName: string;
  tasks: CarrierTask[];
}

function customerPackageLabel(task: CarrierTask): string {
  const customer = [
    task.shipment?.customer?.firstName,
    task.shipment?.customer?.lastName,
  ]
    .filter(Boolean)
    .join(" ");
  const count = task.shipment?.packageCount;
  const packages =
    count === undefined
      ? "Package count unavailable"
      : `${count} package${count === 1 ? "" : "s"}`;
  return customer ? `${customer} · ${packages}` : packages;
}

function timingLabel(task: CarrierTask): string {
  if (task.scheduledWindow) {
    return `${formatDateOnly(task.scheduledWindow.date)} · ${task.scheduledWindow.startTime}–${task.scheduledWindow.endTime}`;
  }
  if (task.readyAt) return `Ready ${formatTaskReadyAt(task)}`;
  if (task.scheduledDate) {
    return `Scheduled ${formatDateOnly(task.scheduledDate)}`;
  }
  return `Created ${formatDateTime(task.createdAt)}`;
}

export function AwaitingReceiptSection({ tasks }: { tasks: CarrierTask[] }) {
  const receiveTasks = useReceiveTasksBulk();
  const submitLock = useRef(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    () => new Set()
  );
  const [submittingCenterId, setSubmittingCenterId] = useState<string | null>(
    null
  );
  const [errorCenterId, setErrorCenterId] = useState<string | null>(null);
  const groups = useMemo(() => {
    const byCenter = new Map<string, ReceiptGroup>();
    for (const task of tasks) {
      const centerId = task.dispatchCenterId ?? task.dispatchCenter?.id ?? null;
      const key = centerId ?? "missing-center";
      const group = byCenter.get(key) ?? {
        centerId,
        centerName: task.dispatchCenter?.name ?? "Dispatch Center unavailable",
        tasks: [],
      };
      group.tasks.push(task);
      byCenter.set(key, group);
    }
    return Array.from(byCenter.values());
  }, [tasks]);

  const toggleTask = (taskId: string, checked: boolean) => {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (checked) next.add(taskId);
      else next.delete(taskId);
      return next;
    });
    setErrorCenterId(null);
  };

  const receiveGroup = async (group: ReceiptGroup) => {
    const taskIds = group.tasks
      .map((task) => task.id)
      .filter((taskId) => selectedTaskIds.has(taskId));
    if (!group.centerId || taskIds.length === 0 || submitLock.current) return;

    submitLock.current = true;
    setSubmittingCenterId(group.centerId);
    setErrorCenterId(null);
    try {
      await receiveTasks.mutateAsync({
        taskIds,
        dispatchCenterId: group.centerId,
      });
      setSelectedTaskIds((current) => {
        const next = new Set(current);
        for (const taskId of taskIds) next.delete(taskId);
        return next;
      });
      toast.success(
        `${taskIds.length} Task${taskIds.length === 1 ? "" : "s"} marked received.`
      );
    } catch {
      setErrorCenterId(group.centerId);
      toast.error("Tasks could not be marked received. Please retry.");
    } finally {
      submitLock.current = false;
      setSubmittingCenterId(null);
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between text-sm font-semibold text-zinc-800">
        <span>Awaiting Receipt</span>
        <Badge variant="secondary">{tasks.length}</Badge>
      </div>
      {tasks.length === 0 ? (
        <DispatchInlineState title="No tasks awaiting receipt." />
      ) : (
        <div className="space-y-2">
          {groups.map((group) => {
            const selectedCount = group.tasks.filter((task) =>
              selectedTaskIds.has(task.id)
            ).length;
            const submitting = submittingCenterId === group.centerId;
            return (
              <div
                key={group.centerId ?? "missing-center"}
                className="overflow-hidden rounded-lg border border-emerald-200 bg-white shadow-sm"
              >
                <div className="flex items-center gap-2 border-b border-emerald-100 bg-emerald-50 px-3 py-2">
                  <PackageCheck className="h-4 w-4 text-emerald-700" />
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-zinc-900">
                    {group.centerName}
                  </span>
                  <Badge className="bg-emerald-100 text-emerald-800">
                    {group.tasks.length}
                  </Badge>
                </div>
                <div className="divide-y divide-zinc-100">
                  {group.tasks.map((task) => (
                    <label
                      key={task.id}
                      htmlFor={`receipt-${task.id}`}
                      className="flex min-h-11 cursor-pointer items-start gap-2.5 px-3 py-2.5 hover:bg-zinc-50"
                    >
                      <Checkbox
                        id={`receipt-${task.id}`}
                        aria-label={`Select ${taskLabel(task)} for receipt`}
                        className="mt-0.5"
                        checked={selectedTaskIds.has(task.id)}
                        disabled={!group.centerId || submitting}
                        onCheckedChange={(checked) =>
                          toggleTask(task.id, checked === true)
                        }
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <TaskReferenceDisplay
                            task={task}
                            className="flex-1"
                            supportingText={
                              task.shipment?.orderNumber
                                ? `ORD-${task.shipment.orderNumber}`
                                : undefined
                            }
                          />
                          <span className="font-mono text-[10px] tabular-nums text-zinc-500">
                            {timingLabel(task)}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-zinc-500">
                          {customerPackageLabel(task)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="border-t border-zinc-100 bg-zinc-50 px-3 py-2">
                  {errorCenterId && errorCenterId === group.centerId && (
                    <div
                      role="alert"
                      className="mb-2 text-xs font-medium text-red-700"
                    >
                      Tasks could not be marked received. Please retry.
                    </div>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    className="h-11 w-full lg:h-8"
                    aria-label={`Mark ${selectedCount} selected Task${selectedCount === 1 ? "" : "s"} received`}
                    disabled={
                      selectedCount === 0 || !group.centerId || submitting
                    }
                    onClick={() => void receiveGroup(group)}
                  >
                    {submitting && <Loader2 className="animate-spin" />}
                    Mark received ({selectedCount})
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
